// Shared date math, navigation helpers, and constants for the calendar
// components. Extracted from pages/CalendarPage.tsx.

import type { CrewRow, AssignmentPurpose } from '@gutterinv/shared';

export type ViewMode = 'month' | '2week' | 'week' | 'day';

// 60s foreground poll for the 4 calendar feeds. Lengthened from 30s to trim
// free-tier Supabase egress (DB query results via the pooler) — a scheduling
// board is set hours/days out, so 60s cross-user propagation is fine
// (2026-06-11 egress-quota mitigation; mutations still invalidate immediately).
export const POLL_INTERVAL_MS = 60_000;

export const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'] as const;

// Drag-and-drop payload shape. Carried on @dnd-kit's `useDraggable({ data })`
// (see useCalendarDnd + calendar-drop) — no native HTML5 DnD / dataTransfer.
//
// Tagged union so the central resolver can branch on payload.kind.
export type CalendarDragPayload =
  | { kind: 'unassigned'; jobId: string; purpose: AssignmentPurpose }
  | {
      kind: 'reassign';
      jobId: string;
      purpose: AssignmentPurpose;
      oldAssignmentId: string;
      durationDays: number;
    }
  // Single-day scheduled task being moved. Tasks have no span (start === end),
  // so the target date matters on drop; `personIds` is the task's CURRENT
  // assignees, so the week view can detect a single-assignee → different-person
  // drop and reassign (drag up/down = reassign). Month view ignores it.
  | { kind: 'task-reassign'; taskId: string; personIds: string[] };

/** Job statuses where the calendar is read-only at the worker level
 *  (INSTALLED_LOCKED). Mirrors the worker's POST/PATCH/DELETE guards
 *  so the UI doesn't show edit affordances for actions the server will
 *  reject with 409. */
export const POST_INSTALL_STATUSES = ['installed', 'invoiced', 'completed'] as const;
export function isPostInstallStatus(status: string): boolean {
  return (POST_INSTALL_STATUSES as readonly string[]).includes(status);
}

/** Statuses at which a NEW install crew may be dispatched — gutter
 *  "Schedule Job" (Card 4) and insulation "Schedule a phase". Accepted is
 *  the lower bound (you can't dispatch install before the customer accepts);
 *  post-install + disqualified are excluded — those are read-only and handled
 *  separately (see isPostInstallStatus). This predicate gates the CREATE
 *  affordance only, not view/edit of existing assignments. */
export const INSTALL_SCHEDULABLE_STATUSES = ['accepted', 'scheduled'] as const;
export function isInstallSchedulable(status: string): boolean {
  return (INSTALL_SCHEDULABLE_STATUSES as readonly string[]).includes(status);
}

// Default crew stubs used when the API hasn't returned crews yet
export const DEFAULT_CREWS: CrewRow[] = [
  { id: 'alpha', name: 'Alpha', color: '#3b82f6', displayOrder: 0, active: true },
  { id: 'bravo', name: 'Bravo', color: '#10b981', displayOrder: 1, active: true },
  { id: 'charlie', name: 'Charlie', color: '#f59e0b', displayOrder: 2, active: true },
];

/** Monday of the week containing `d` (ISO weeks start Monday). */
export function startOfWeek(d: Date): Date {
  const result = new Date(d);
  const day = result.getDay(); // 0=Sun … 6=Sat
  const diff = day === 0 ? -6 : 1 - day;
  result.setDate(result.getDate() + diff);
  result.setHours(0, 0, 0, 0);
  return result;
}

/** Sunday of the week containing `d`. */
export function endOfWeek(d: Date): Date {
  const mon = startOfWeek(d);
  const sun = new Date(mon);
  sun.setDate(mon.getDate() + 6);
  sun.setHours(23, 59, 59, 999);
  return sun;
}

/** First day of the month containing `d`. */
export function startOfMonth(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), 1);
}

/** Last day of the month containing `d`. */
export function endOfMonth(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth() + 1, 0, 23, 59, 59, 999);
}

/** YYYY-MM-DD for a Date. */
export function toISO(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** Short month name + day, e.g. "Apr 14". */
export function shortDate(d: Date): string {
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

/** Full month + year, e.g. "April 2026". */
export function monthYear(d: Date): string {
  return d.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
}

/** Generate an array of Dates from `start` for `count` days. */
export function dateRange(start: Date, count: number): Date[] {
  const result: Date[] = [];
  for (let i = 0; i < count; i++) {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    result.push(d);
  }
  return result;
}

export function addDays(d: Date, n: number): Date {
  const r = new Date(d);
  r.setDate(r.getDate() + n);
  return r;
}

export function addMonths(d: Date, n: number): Date {
  const r = new Date(d);
  r.setMonth(r.getMonth() + n);
  return r;
}

export function isSameDay(a: Date, b: Date): boolean {
  return toISO(a) === toISO(b);
}

export function isToday(d: Date): boolean {
  return isSameDay(d, new Date());
}

export interface DateWindow {
  from: Date;
  to: Date;
  label: string;
  days: Date[];
}

export function getDateWindow(anchor: Date, mode: ViewMode): DateWindow {
  if (mode === 'day') {
    const from = new Date(anchor);
    from.setHours(0, 0, 0, 0);
    const to = new Date(anchor);
    to.setHours(23, 59, 59, 999);
    return {
      from,
      to,
      label: from.toLocaleDateString('en-US', {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      }),
      days: [from],
    };
  }
  if (mode === 'week') {
    const from = startOfWeek(anchor);
    const to = endOfWeek(anchor);
    return {
      from,
      to,
      label: `${shortDate(from)} - ${shortDate(to)}, ${to.getFullYear()}`,
      days: dateRange(from, 7),
    };
  }
  if (mode === '2week') {
    const from = startOfWeek(anchor);
    const to = addDays(from, 13);
    to.setHours(23, 59, 59, 999);
    return {
      from,
      to,
      label: `${shortDate(from)} - ${shortDate(to)}, ${to.getFullYear()}`,
      days: dateRange(from, 14),
    };
  }
  // month
  const mStart = startOfMonth(anchor);
  const mEnd = endOfMonth(anchor);
  const from = startOfWeek(mStart);
  // fill out to complete the last week row
  const rawEnd = endOfWeek(mEnd);
  const dayCount =
    Math.round((rawEnd.getTime() - from.getTime()) / (1000 * 60 * 60 * 24)) + 1;
  return {
    from,
    to: rawEnd,
    label: monthYear(anchor),
    days: dateRange(from, dayCount),
  };
}

export function navigateWindow(anchor: Date, mode: ViewMode, direction: -1 | 1): Date {
  if (mode === 'day') return addDays(anchor, direction);
  if (mode === 'week') return addDays(anchor, direction * 7);
  // 2-week view slides by ONE week per click so adjacent windows
  // overlap by a week — the office sees continuity rather than
  // jumping past 14 days at once.
  if (mode === '2week') return addDays(anchor, direction * 7);
  return addMonths(anchor, direction);
}

/** Yield each YYYY-MM-DD from startISO..endISO inclusive. UTC-based so
 *  daylight-saving transitions can't drop or duplicate a day. */
export function* iterateDaysInclusive(startISO: string, endISO: string): Iterable<string> {
  if (startISO > endISO) return;
  const start = new Date(`${startISO}T00:00:00Z`);
  const end = new Date(`${endISO}T00:00:00Z`);
  for (let d = start; d.getTime() <= end.getTime(); d = new Date(d.getTime() + 86_400_000)) {
    yield d.toISOString().slice(0, 10);
  }
}

/** Count days in [startISO..endISO] inclusive (1 for single-day). */
export function daySpanInclusive(startISO: string, endISO: string): number {
  if (startISO > endISO) return 0;
  const start = new Date(`${startISO}T00:00:00Z`).getTime();
  const end = new Date(`${endISO}T00:00:00Z`).getTime();
  return Math.round((end - start) / 86_400_000) + 1;
}

/** Add `n` days to a YYYY-MM-DD ISO string and return the new ISO. */
export function addDaysISO(iso: string, n: number): string {
  const d = new Date(`${iso}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}

/** Blend a hex color (e.g. "#3B82F6") with white at the given alpha and
 *  return a SOLID rgb() string. Used for calendar chip backgrounds so
 *  the day-cell's today/weekend tint can't bleed through a translucent
 *  fill. `alpha` is the crew-color fraction in [0..1]; the rest is white. */
export function mixWithWhite(hex: string, alpha: number): string {
  // Tolerate missing # and short-form #rgb.
  let s = hex.startsWith('#') ? hex.slice(1) : hex;
  if (s.length === 3) s = s.split('').map((c) => c + c).join('');
  if (s.length !== 6) return 'rgb(255,255,255)';
  const r = parseInt(s.slice(0, 2), 16);
  const g = parseInt(s.slice(2, 4), 16);
  const b = parseInt(s.slice(4, 6), 16);
  const w = 1 - alpha;
  return `rgb(${Math.round(r * alpha + 255 * w)},${Math.round(g * alpha + 255 * w)},${Math.round(b * alpha + 255 * w)})`;
}
